import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box, Divider } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";

import { bulkIngestDocuments } from "api";
import { EsgDownloadIcon, EsgFileUpload } from "assets";
import { useNotify, useSites } from "hooks";
import { useUserRole } from "recoils";
import { QUERY_KEYS } from "consts";
import { updateFileCountContext } from "providers/uploadFileCount-provider";
import { Body1Regular, Body2Regular, CloseButton, COLORS, DropZone } from "components";
import { extractErrorMessage } from "utils";
import { DEFAULT_SITE_ID, MAX_TOTAL_UPLOAD_SIZE_BYTES, SERVER_LIMIT_EXCEEDED_ERROR_MESSAGE } from "./const";
import { EsgDocumentsUploadHandle } from "./types";

export type { EsgDocumentsUploadHandle } from "./types";

export const EsgDocumentsUpload = forwardRef<
	EsgDocumentsUploadHandle,
	{ onUploadCompleted: () => void; siteId?: number }
>(({ onUploadCompleted, siteId }, ref) => {
	const notify = useNotify();
	const userRole = useUserRole();
	const queryClient = useQueryClient();
	const { data: sites } = useSites();

	const resetCounterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const [uploadDisable, setUploadDisable] = useState<boolean>(false);
	const [openUploadModal, setOpenUploadModal] = useState<boolean>(false);
	const [openFirstDialog, setOpenFirstDialog] = useState<boolean>(false);
	const [openSecondDialog, setOpenSecondDialog] = useState<boolean>(false);
	const [openCancelConfirmDialog, setOpenCancelConfirmDialog] = useState<boolean>(false);
	const [pendingDropFiles, setPendingDropFiles] = useState<File[]>([]);

	const { updateUploadedCount, updateTotalCount } = useContext(updateFileCountContext);

	useImperativeHandle(ref, () => ({
		openExcelDialogWithFiles(files: File[]) {
			setPendingDropFiles(files);
			setOpenFirstDialog(true);
		},
	}));

	const uploadWithBulkIngest = useCallback(
		async (files: File[], override = false) => {
			const totalSize = files.reduce((sum, f) => sum + f.size, 0);
			if (totalSize > MAX_TOTAL_UPLOAD_SIZE_BYTES) {
				notify.error(SERVER_LIMIT_EXCEEDED_ERROR_MESSAGE);
				return;
			}

			clearTimeout(resetCounterTimeoutRef.current ?? undefined);
			updateUploadedCount(0);
			updateTotalCount(files.length);
			setUploadDisable(true);
			try {
				const result = await bulkIngestDocuments(siteId ?? DEFAULT_SITE_ID, files, override);
				if (result.status === "ERROR") {
					notify.error(extractErrorMessage(result, `Failed to upload files: ${result.message}`));
					updateTotalCount(0);
					return;
				}
				notify.success(result.message ?? "Successfully uploaded all files.");
				updateUploadedCount(files.length);
				clearTimeout(resetCounterTimeoutRef.current ?? undefined);
				resetCounterTimeoutRef.current = setTimeout(() => {
					updateUploadedCount(0);
					updateTotalCount(0);
				}, 3000);
				onUploadCompleted();
				queryClient.invalidateQueries([QUERY_KEYS.DOCUMENTS]);
			} catch (error: unknown){
				const status = (error as any)?.response?.status;
				notify.error(
					status === 413
						? SERVER_LIMIT_EXCEEDED_ERROR_MESSAGE
						: extractErrorMessage(error, "Failed to upload files. Please try again.")
				);
				updateTotalCount(0);
			} finally {
				setUploadDisable(false);
				setPendingDropFiles([]);
			}
		},
		[notify, onUploadCompleted, queryClient, siteId, updateTotalCount, updateUploadedCount],
	);

	const handleExcelDrop = (files: File[]) => {
		setOpenUploadModal(false);
		setPendingDropFiles(files);
		setOpenFirstDialog(true);
	};

	const handleDropZoneUploadCompleted = () => {
		setOpenUploadModal(false);
		onUploadCompleted();
	};

	const handleDialogCancel = () => {
		setOpenCancelConfirmDialog(true);
	};

	const handleConfirmCancel = () => {
		setOpenCancelConfirmDialog(false);
		setOpenFirstDialog(false);
		setOpenSecondDialog(false);
		setPendingDropFiles([]);
	};

	const handleGoBack = () => {
		setOpenCancelConfirmDialog(false);
	};

	const handleFirstDialogClose = (choice: "Yes" | "Not Sure" | "No") => {
		setOpenFirstDialog(false);
		if (choice === "Yes") {
			setOpenSecondDialog(true);
		} else {
			uploadWithBulkIngest(pendingDropFiles, false);
		}
	};

	const handleSecondDialogClose = (choice: "Yes" | "No") => {
		setOpenSecondDialog(false);
		const shouldOverride = choice === "Yes";
		uploadWithBulkIngest(pendingDropFiles, shouldOverride);
	};

	useEffect(() => () => {
		clearTimeout(resetCounterTimeoutRef.current ?? undefined);
	}, []);

	return (
		<>
			<Box
				data-testid="upload-box"
				sx={{ display: "flex", flexDirection: "column", gap: 1, width: "200px", position: "relative" }}>
				<Button
					sx={{
						borderRadius: 2,
						color: "white",
						width: "200px",
						height: "36px",
						p: 0

					}}
					variant="contained"
					data-testid="upload-data-button"
					startIcon={<EsgFileUpload />}
					disabled={!sites?.length || userRole.isAuditor || uploadDisable}
					onClick={() => setOpenUploadModal(true)}>
					<Body2Regular>Upload Data</Body2Regular>
				</Button>
			</Box>

			{/* Upload Modal */}
			<Dialog
				data-testid="upload-modal"
				open={openUploadModal}
				onClose={() => setOpenUploadModal(false)}
				PaperProps={{
					sx: {
						width: 580,
						bgcolor: "#FFFFFF",
					},
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2 }}>
					<DialogTitle sx={{ fontSize: '16px', p: 0, pl: 1 }}>
						Upload Templatized Data
					</DialogTitle>
					<CloseButton onClick={() => setOpenUploadModal(false)} />
				</Box>
				<Divider />
				<DialogContent>
					<DropZone
						siteId={siteId}
						onUploadCompleted={handleDropZoneUploadCompleted}
						onExcelDrop={handleExcelDrop}
						rejectMessageMode="dropzone"
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								minHeight: 320,
								height: "fit-content",
								border: "4px dashed #C1C1C1",
								borderRadius: "8px",
								gap: 4,
								cursor: "pointer",
							}}
						>
							<Box
								sx={{
									width: 48,
									height: 48,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<EsgDownloadIcon color={COLORS.Romance} width={48} height={48} />
							</Box>
							<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
								<Body1Regular sx={{ fontSize: "24px", fontWeight: 500 }}>Drag & drop any files here</Body1Regular>
								<Body2Regular sx={{ fontSize: "16px", fontWeight: 400, color: COLORS.Romance }}>
									or <b>browse files</b> from device
								</Body2Regular>
							</Box>
						</Box>
					</DropZone>
				</DialogContent>
			</Dialog>

			{/* First Dialog */}
			<Dialog
				data-testid="first-dialog"
				open={openFirstDialog}
				onClose={handleDialogCancel}
				PaperProps={{
					sx: {
						width: 384,
						bgcolor: "#FFFFFF",
					},
				}}
			>
				<DialogContent sx={{ py: '2px', pl: 3 }}>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<ErrorOutline sx={{ color: "#C55050" }} />
						<DialogTitle sx={{ px: '10px', fontSize: '16px', fontWeight: 600, fontFamily: 'Inter', m: 0 }}>
							Data Points Confirmed?
						</DialogTitle>
					</Box>
					<DialogContentText sx={{ fontSize: '14px', fontWeight: 400, fontFamily: 'Inter', opacity: 0.65 }}>
						Are you uploading confirmed data points (e.g., historically verified data, data from metering systems)?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ color: "#6B7280", fontSize: '14px', fontWeight: 600 }}
						onClick={() => handleFirstDialogClose("Not Sure")}
						variant="text"
						data-testid="first-dialog-not-sure"
					>
						Not Sure
					</Button>
					<Button
						sx={{ color: "#6B7280", fontSize: '14px', fontWeight: 600 }}
						onClick={() => handleFirstDialogClose("No")}
						variant="text"
						data-testid="first-dialog-no"
					>
						No
					</Button>
					<Button
						sx={{
							borderRadius: "8px",
							background: "#C55050",
							color: "white",
							fontSize: '14px',
							fontWeight: 600,
							"&:hover": { background: "#C55050" },
						}}
						onClick={() => handleFirstDialogClose("Yes")}
						variant="contained"
						data-testid="first-dialog-yes"
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>


			{/* Second Dialog */}
			<Dialog
				data-testid="second-dialog"
				open={openSecondDialog} onClose={handleDialogCancel}
				PaperProps={{
					sx: {
						width: 384,
						bgcolor: "#FFFFFF",
					},
				}}
			>
				<DialogContent sx={{ py: '2px', pl: 3 }}>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<ErrorOutline sx={{ color: "#C55050" }} />
						<DialogTitle sx={{ px: '10px', fontSize: '16px', fontWeight: 600, fontFamily: 'Inter', m: 0 }}>
							Override All Flags?
						</DialogTitle>
					</Box>
					<DialogContentText sx={{ fontSize: '14px', fontWeight: 400, fontFamily: 'Inter', opacity: 0.65 }}>
						Do you want to override all anomaly flags? (e.g. Duplicate detection, YoY/MoM threshold detection, etc.)
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ color: "#6B7280", fontSize: '14px', fontWeight: 600 }}
						onClick={() => handleSecondDialogClose("No")}
						variant="text"
						data-testid="second-dialog-no"
					>
						No
					</Button>
					<Button
						sx={{
							borderRadius: "8px",
							background: "#C55050",
							color: "white",
							fontSize: '14px',
							fontWeight: 600,
							"&:hover": { background: "#C55050" },
						}}
						onClick={() => handleSecondDialogClose("Yes")}
						variant="contained"
						data-testid="second-dialog-yes"
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>

			{/* Cancel Confirmation Dialog */}
			<Dialog
				data-testid="cancel-confirm-dialog"
				open={openCancelConfirmDialog}
				onClose={handleGoBack}
				PaperProps={{
					sx: {
						width: 384,
						bgcolor: "#FFFFFF",
					},
				}}
			>
				<DialogContent sx={{ py: '2px', pl: 3 }}>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<ErrorOutline sx={{ color: "#C55050" }} />
						<DialogTitle sx={{ px: '10px', fontSize: '16px', fontWeight: 600, fontFamily: 'Inter', m: 0 }}>
							Cancel Upload?
						</DialogTitle>
					</Box>
					<DialogContentText sx={{ fontSize: '14px', fontWeight: 400, fontFamily: 'Inter', opacity: 0.65 }}>
						Are you sure you want to cancel? Your file will not be uploaded.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ color: "#6B7280", fontSize: '14px', fontWeight: 600 }}
						onClick={handleGoBack}
						variant="text"
						data-testid="cancel-confirm-go-back"
					>
						Go Back
					</Button>
					<Button
						sx={{
							borderRadius: "8px",
							background: "#C55050",
							color: "white",
							fontSize: '14px',
							fontWeight: 600,
							"&:hover": { background: "#C55050" },
						}}
						onClick={handleConfirmCancel}
						variant="contained"
						data-testid="cancel-confirm-yes"
					>
						Yes, Cancel
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
});
